import { forwardRef, useCallback, useImperativeHandle, useRef, useState } from "react";
import { isSAWDomain, isSAWPlan } from "@/utils";
import { Button, Form, Input, Tooltip } from "antd";
import { useTranslation } from "react-i18next";
import { apiCheckTenant, apiLookupTenant } from "@/services/workspace.api";
import styles from './index.module.less';
import { debounce } from "lodash";

export type TenantFormValues = {
	realm: string
}

export type Realm = {
	name: string
	domain?: string
}

export enum ClientType {
	Ext = 'extension', // 浏览器插件登录
	Desktop = 'desktop', // 桌面应用
	Mobile = 'mobile', // 手机app
	web = 'web', // 普通网页
}

export interface TenantProps {
    plan_type: string;
    realms: Realm[];
}

export interface TenantRef {
    triggerRealm(): Promise<void>;
    setFieldsValue(values: TenantFormValues): void;
}

export default forwardRef<TenantRef, TenantProps>(function Tenant(props, ref) {
    const { t } = useTranslation();
    const { plan_type, realms } = props;
    const [loading, setLoading] = useState<boolean>(false);
    const [url, setUrl] = useState('');
    const [visible, setVisible] = useState(false);
    const [domain, setDomain] = useState(window.location.host);
    const resultRef = useRef<any>();
    const [form] = Form.useForm<TenantFormValues>();

    const triggerRealm = useCallback(async () => {
        const values = await form.validateFields();
        const realm = values.realm.trim().toLocaleLowerCase();
        const index = realms.findIndex((item: Realm) => item.name === realm);
        const realmObj = { name: realm, isCurrent: true, domain: '' };

        // 插入realm
        // 如果已经在realm中存在
        if (index > -1) {
            realms.splice(index, 1);
        }

        realms.unshift(realmObj);
        // 存储realm到本地
        // 记录切换的realm
        localStorage.realm = realm;

        const currentURL = new URL(window.location.href);
        const workspaceURL = new URL(resultRef.current.domain);

        // 填入租户domain
        realmObj.domain = workspaceURL.host;
        // 修改hash与参数
        workspaceURL.pathname = currentURL.pathname;

        // 如果是desktop登录或mobile登录
        const auth = currentURL.searchParams.get('auth');
        if (auth && (ClientType.Desktop === auth || (ClientType.Mobile === auth && !window.onMobile))) {
            workspaceURL.pathname = 'web-authentication.html';
        }

        workspaceURL.search = currentURL.search;
        workspaceURL.hash = currentURL.hash;

        // 删除auto 自动跳转到第一个最近使用的tenant
        workspaceURL.searchParams.delete('auto');
        // 跳转
        window.location.href = workspaceURL.toString();

        localStorage.realms = JSON.stringify(realms);
    }, [form, realms]);

    useImperativeHandle(ref, () => ({
        triggerRealm,
        setFieldsValue(values: TenantFormValues) {
            form.setFieldsValue(values);
        }
    }), [triggerRealm]);

    const handleSubmit = useCallback(async () => {
        try {
            setLoading(true);
            await triggerRealm();
        } finally {
            setLoading(false);
        }
	}, [triggerRealm]);

    function checkOrganization(name: string) {
        return apiLookupTenant({ name }).then((result: any) => {
            if (name.indexOf('-') > -1) {
                const url = new URL(result.data.domain);
                const [, ...hosts] = url.host.split('.');

                url.host = hosts.join('.');

                return apiCheckTenant(name, url.toString()).then((res: any) => {
                    if (res.data.exists) {
                        return result;
                    }

                    return { code: 500 };
                });
            }

            return result;
        });
    }

    const onChange = debounce((event: any) => {
        const value = event.target.value;

        if (value) {
            setLoading(true);

            return form.validateFields(['realm']).then(() => {
                const data = resultRef.current;
                const domainURL = new URL(data.domain);
                const [, ...rest] = domainURL.host.split('.');

                setVisible(true);
                setUrl(data.domain);
                setDomain(rest.join('.'));
                setLoading(false);
            }).catch((error: any) => {
                console.error(error);
                setLoading(false);
                setVisible(false);
                return Promise.resolve();
            });

        } else {
            setVisible(false);
        }
    }, 300);

    return (
        <Form form={form} onFinish={() => {/**? */}}>
            <Tooltip title={url} placement="top" overlayClassName={styles.popup} open={visible}>
                <Form.Item
                    hasFeedback
                    name="realm"
                    rules={[{
                        required: true,
                        message: t('workspace.rule'),
                    }, {
                        validator: (_, value) => {
                            if (value) {
                                value = value.trim().toLocaleLowerCase();

                                return checkOrganization(value).then((res: any) => {
                                    if (res.code === 0 || !value) {
                                        resultRef.current = res.data;

                                        return Promise.resolve();
                                    }

                                    return Promise.reject('');
                                }).catch(() => {
                                    if (isSAWPlan(plan_type) || isSAWDomain()) {
                                        return Promise.reject(t("domain.invalid"));
                                    }
                                    return Promise.reject(t('workspace.invalid'));
                                });
                            }
                            return Promise.resolve();
                        },
                        validateTrigger: 'onSubmit',
                    }]}
                >
                    <Input
                        onChange={onChange}
                        size="large"
                        addonBefore={<span className={styles.before}>https://</span>}
                        placeholder={!(isSAWPlan(plan_type) || isSAWDomain()) ? t('workspace.placeholder') : "Domain"}
                        addonAfter={
                            <span className={styles.after}>.{domain}</span>
                        }
                    />
                </Form.Item>
            </Tooltip>
            <Form.Item>
                <Button
                    onClick={handleSubmit}
                    block
                    type="primary"
                    size="large"
                    loading={loading}
                >
                    {t('workspace.continue')}
                </Button>
            </Form.Item>
        </Form>
    )
});
